import React, { useRef, useState } from 'react';
import { Share2 } from 'lucide-react';
import { Button } from './Button.jsx';
import Modal from './Modal.jsx';
import { InlineFeedback } from '../feedback/InlineFeedback.jsx';

function plainText(value) {
  const text = String(value || '');
  if (typeof document === 'undefined') return text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const parsed = new DOMParser().parseFromString(text, 'text/html');
  parsed.querySelectorAll('script,style').forEach((node) => node.remove());
  return (parsed.body.textContent || '').replace(/\s+/g, ' ').trim();
}

function validUrl(value) {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : '';
  } catch { return ''; }
}

/** Share public content. Callers supply the published canonical URL, never a preview URL. */
export function ShareButton({ contentType = 'content', title, description, image, canonicalUrl, children, disabled = false, variant = 'outline', size = 'sm', ...buttonProps }) {
  const trigger = useRef(null);
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [failedImage, setFailedImage] = useState(null);
  const url = validUrl(canonicalUrl);
  const shareTitle = plainText(title);
  const text = plainText(description);
  const shareText = [shareTitle, text].filter(Boolean).join('\n');
  const label = `Share ${contentType}`;

  async function share() {
    if (!url || pending.current) return;
    pending.current = true;
    setBusy(true);
    setMessage('');
    try {
      if (typeof navigator.share === 'function') {
        try {
          await navigator.share({ title: shareTitle, text, url });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }
      setOpen(true);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setMessage(`${contentType.charAt(0).toUpperCase()}${contentType.slice(1)} link copied to clipboard.`);
    } catch {
      setMessage('Select and copy the link below to share it.');
    }
  }

  const links = [
    ['Email', `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent([shareText, url].filter(Boolean).join('\n\n'))}`],
    ['Facebook', `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`],
    ['WhatsApp', `https://wa.me/?text=${encodeURIComponent([shareText, url].filter(Boolean).join('\n\n'))}`],
    ['X', `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(url)}`],
  ];

  return <>
    <Button {...buttonProps} ref={trigger} type="button" variant={variant} size={size} disabled={disabled || !url || busy} aria-label={label} aria-busy={busy} onClick={share}>
      {children ?? <><Share2 size={16} aria-hidden="true" /><span className="ml-1">Share</span></>}
    </Button>
    <Modal open={open} onOpenChange={setOpen} onCloseAutoFocus={(event) => { event.preventDefault(); trigger.current?.focus(); }} className="max-h-[90dvh] overflow-y-auto">
      <Modal.Header onClose={() => setOpen(false)} closeLabel="Close share dialog">
        <Modal.Title>{label}</Modal.Title>
        <Modal.Description>Choose how to share this {contentType}.</Modal.Description>
      </Modal.Header>
      <Modal.Body className="space-y-4">
        {image && image !== failedImage ? <img src={image} alt="" className="aspect-video w-full rounded-md object-cover" onError={() => setFailedImage(image)} /> : null}
        <div><p className="break-words font-semibold text-gray-900">{shareTitle}</p>{text ? <p className="mt-1 line-clamp-3 text-sm text-gray-600">{text}</p> : null}</div>
        <div className="flex flex-wrap gap-3">
          <Button size="sm" onClick={copy}>Copy link</Button>
          {links.map(([name, href]) => <a key={name} href={href} target={name === 'Email' ? undefined : '_blank'} rel="noopener noreferrer" className="rounded-md border px-3 py-2 text-sm font-medium text-blue-700 hover:underline focus-visible:outline focus-visible:outline-2">{name}</a>)}
        </div>
        <InlineFeedback variant="status">{message}</InlineFeedback>
        <label className="block text-sm text-gray-700">Canonical link<input aria-label="Canonical link" readOnly value={url} onFocus={(event) => event.currentTarget.select()} className="mt-1 w-full rounded-md border p-2 text-sm" /></label>
      </Modal.Body>
    </Modal>
  </>;
}
